import { useSubpantalla } from '../ui/navegacionReact';
import {
 useCallback, useEffect, useState } from 'react';
import {
  FileText, Plus, Check, Clock, AlertTriangle, Download, X, Users,
} from 'lucide-react';
import { api, ErrorApi } from '../api';
import NotaExplicativa from '../ui/NotaExplicativa';
import { TIPOS_DOCUMENTO, fechaCorta } from '../ui/documentos';
import FichaDocumento from './FichaDocumento';
import { useAuth } from '../lib/contextoAuth';
import { Cargando } from '../ui/EstadoDePantalla';

// CONTROL DE DOCUMENTOS · Fase 2 del módulo de Calidad.
//
// QUÉ PREGUNTA CONTESTA ESTA PANTALLA. Un auditor pide tres cosas: la
// revisión vigente (una, no dos), quién la ha leído, y la anterior con
// algo que la marque como vieja. Las tres se contestan desde aquí.
//
// TRES DECISIONES DE USUARIO CERO QUE GOBIERNAN ESTO:
//
//   1. **La revisión vigente va EN LA LISTA**, no un clic más adentro.
//      Es el dato que la gente viene a buscar; una lista de códigos y
//      títulos obliga a abrir uno por uno para saber por cuál va cada
//      cosa.
//   2. **El estado de cada revisión se dice con palabras y color**, no
//      solo con color: verde=vigente, ámbar=borrador, gris=obsoleta, y
//      cada una con su etiqueta al lado (Doctrina 2, regla 2).
//   3. **Aprobar avisa de la consecuencia ANTES**, porque obliga a toda
//      la planta a releer un procedimiento. Es una acción riesgosa y
//      pide confirmación explicando qué pasa (regla 7).

// ---------------------------------------------------------------------
// Alta de un documento
// ---------------------------------------------------------------------
function FormularioAlta({ onCreado, onCancelar }) {
  const [codigo, setCodigo] = useState('');
  const [titulo, setTitulo] = useState('');
  const [tipo, setTipo] = useState('PROCEDIMIENTO');
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);

  // VALIDACIÓN EN VIVO, no al guardar (Doctrina 2, regla 8). El botón se
  // deshabilita Y SE DICE POR QUÉ debajo: un botón gris sin explicación
  // deja a la persona probando cosas.
  const falta = !codigo.trim() ? 'el código'
    : titulo.trim().length < 2 ? 'el título'
    : null;

  async function enviar(e) {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    try {
      const d = await api.post('/documentos', {
        codigo, titulo, tipo,
      });
      onCreado(d);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form onSubmit={enviar} className="border border-gray-200 rounded-xl p-4 space-y-3 bg-white">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-900">Dar de alta un documento</h3>
        <button type="button" onClick={onCancelar}
                className="text-gray-500 hover:text-gray-700" aria-label="Cancelar">
          <X size={18} />
        </button>
      </div>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700">Código</span>
        <input
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          placeholder="PR-CAL-001"
          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <span className="block text-xs text-gray-500 mt-1">
          Como lo llama la planta en voz alta. Se guarda en mayúsculas y sin
          espacios sobrantes.
        </span>
      </label>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700">Título</span>
        <input
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="Inspección de entrada"
          className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
      </label>

      <label className="block">
        <span className="block text-sm font-medium text-gray-700">Tipo</span>
        <select value={tipo} onChange={(e) => setTipo(e.target.value)}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
          {TIPOS_DOCUMENTO.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
        </select>
      </label>

      <p className="text-xs text-gray-500">
        Lo demás —responsable, proceso, cuándo toca revisarlo— se puede poner
        después desde la ficha.
      </p>

      {error ? (
        <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
          {error}
        </p>
      ) : null}

      <div className="flex items-center gap-3">
        <button type="submit" disabled={!!falta || guardando}
                className="bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50">
          {guardando ? 'Guardando…' : 'Dar de alta'}
        </button>
        {falta ? (
          <span className="text-xs text-gray-500">Falta {falta}.</span>
        ) : null}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------
// La lista
// ---------------------------------------------------------------------
export default function Documentos() {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [alta, setAlta] = useState(false);
  const [msg, setMsg] = useState(null);
  // La ficha vive DENTRO de la lista, como `FichaProducto` dentro de
  // `Productos`: es el patrón de la casa y evita una ruta más.
  const [abierto, setAbierto] = useState(null);
  useSubpantalla('documento', !!abierto, () => setAbierto(null), { valor: abierto, abrir: setAbierto });
  const { puede } = useAuth();   // puede ser undefined en cuenta personal

  const cargar = useCallback(() => {
    api.get('/documentos')
      .then(setDatos)
      .catch((e) => setError(e.message));
  }, []);

  useEffect(cargar, [cargar]);

  if (error && !datos) {
    return (
      <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">
        {error}
      </div>
    );
  }
  if (!datos) return <Cargando filas={4} />;

  if (abierto) {
    return (
      <FichaDocumento
        id={abierto}
        onVolver={() => { setAbierto(null); cargar(); }}
        puedeEditar={puede ? puede('documentos.editar') : false}
        puedeAprobar={puede ? puede('documentos.aprobar') : false}
      />
    );
  }

  const lista = datos.documentos;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Documentos</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Los procedimientos, instrucciones y formatos de la empresa, con la
            revisión que está en vigor ahora mismo.
          </p>
        </div>
        <button onClick={() => setAlta(true)}
                className="shrink-0 inline-flex items-center gap-1.5 bg-blue-600 text-white text-sm font-medium px-3 py-2 rounded-lg">
          <Plus size={16} aria-hidden="true" /> Nuevo
        </button>
      </div>

      {msg ? (
        <p role="status" className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">
          {msg}
        </p>
      ) : null}

      {alta ? (
        <FormularioAlta
          onCancelar={() => setAlta(false)}
          onCreado={(d) => {
            setAlta(false);
            setMsg(d.mensaje);
            cargar();
          }}
        />
      ) : null}

      {/* ESTADO VACÍO ÚTIL (Doctrina 2, regla 6): qué es esto, por qué
          está vacío, y el botón con la acción sugerida. */}
      {lista.length === 0 && !alta ? (
        <div className="border border-dashed border-gray-300 rounded-xl p-6 text-center">
          <FileText size={28} className="mx-auto text-gray-400" aria-hidden="true" />
          <p className="mt-2 font-medium text-gray-900">
            Sin documentos controlados
          </p>
          <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
            Aquí viven los procedimientos e instrucciones que tu gente tiene que
            seguir. TEGO guarda cada versión, controla que solo una esté
            vigente, y registra quién la ha leído.
          </p>
          <button onClick={() => setAlta(true)}
                  className="mt-4 inline-flex items-center gap-1.5 bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-lg">
            <Plus size={16} aria-hidden="true" /> Dar de alta el primero
          </button>
        </div>
      ) : null}

      {lista.length > 0 ? (
        <NotaExplicativa titulo="Para qué sirve esto">
          Un auditor pregunta tres cosas: cuál es la revisión vigente, quién la
          ha leído, y que la anterior se distinga de la nueva. TEGO impide que
          haya dos revisiones vigentes a la vez, guarda quién acusó cada una, y
          estampa <strong>OBSOLETO</strong> encima de las viejas al descargarlas.
        </NotaExplicativa>
      ) : null}

      <div className="space-y-2">
        {lista.map((d) => (
          <button
            key={d.id}
            onClick={() => setAbierto(d.id)}
            className="w-full text-left border border-gray-200 rounded-xl p-3 bg-white hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-semibold text-gray-900">
                {d.codigo}
              </span>
              <span className="text-sm text-gray-900">{d.titulo}</span>
              {d.estado === 'RETIRADO' ? (
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                  Retirado
                </span>
              ) : null}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
              <span>{d.tipo_nombre}</span>
              {d.proceso ? <span>· {d.proceso}</span> : null}
              {d.revision_vigente ? (
                <span className="inline-flex items-center gap-1 text-green-800">
                  · Revisión {d.revision_vigente}
                  {d.revision_fecha ? <> del {fechaCorta(d.revision_fecha)}</> : null}
                </span>
              ) : (
                // NO SE DEJA EN BLANCO. «Sin revisión aprobada» es una
                // respuesta; un hueco vacío es una pregunta.
                <span className="inline-flex items-center gap-1 text-amber-800">
                  · Sin revisión aprobada todavía
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
